<template>
  <section class="safe-insets grid min-h-screen place-items-center bg-hazel-50 pt-11">
    <div class="mx-auto w-full max-w-[28rem] px-4">
      <div class="space-y-6 overflow-hidden rounded-lg bg-white p-8">
        <div>
          <img src="@/assets/carvia_logo.svg" alt="Logo" class="mx-auto mb-8 w-64 max-w-full" />
          <h1 class="text-center font-medium">
            {{ t('welcomeBack') }}
          </h1>
        </div>
        <div>
          <div v-if="isOAuthLoading">
            <Spinner class="mx-auto mb-4" />
            <div class="text-center text-gray-500">{{ t('loggingIn') }}</div>
          </div>
          <PasswordForgotForm
            v-else-if="authPanel === 'forgotPassword'"
            ref="passwordForgotForm"
            :is-loading="sendingPasswordReset"
            @on-send-password-submit="onPasswordReset"
            @on-switch-to-login="authPanel = 'login'"
          />

          <DevicePairingForm
            v-else-if="authPanel === 'devicePairing'"
            :is-loading="isDevicePairingPending"
            :pairing-error="pairingError"
            @on-pair-device-submit="onDevicePairing"
            @on-switch-to-login="authPanel = 'login'"
          />

          <LoginForm
            v-else
            :is-loading="isPending"
            :login-error="loginError"
            @on-login="onLogin"
            @on-switch-to-password-forgot="authPanel = 'forgotPassword'"
          />
        </div>
      </div>
      <p v-if="authPanel === 'login' && !isOAuthLoading" class="mt-4 text-center">
        <button
          type="button"
          class="text-sm text-gray-500 hover:text-gray-700"
          @click="authPanel = 'devicePairing'"
        >
          {{ t('pairDeviceLink') }}
        </button>
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { LoginDto } from '@/entities/auth/login-dto.entity';
import type { SendPasswordResetMailDto } from '@/entities/auth/send-password-reset-mail.dto';
import { useI18n } from 'vue-i18n';
import { useLogin, useOAuthCallback } from '@/queries/auth/use-login';
import { useSendPasswordResetMail } from '@/queries/auth/use-password-reset';
import router from '@/router';
import { onMounted, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import LoginForm from './components/LoginForm.vue';
import PasswordForgotForm from './components/PasswordForgotForm.vue';
import DevicePairingForm from './components/DevicePairingForm.vue';
import { useExchangeDeviceAuthCode } from '@/queries/auth/use-device-auth';

const STAGING_APP_ORIGIN = 'https://ros.carvia.dev';

function getCredentialsFromQuery(query: typeof router.currentRoute.value.query): LoginDto | null {
  const password = typeof query.password === 'string' ? query.password : null;
  const email = (typeof query.email === 'string' && query.email) || null;
  if (!email || !password) return null;
  return { email, password };
}

const { t } = useI18n();

type AuthPanel = 'login' | 'forgotPassword' | 'devicePairing';
const authPanel = ref<AuthPanel>('login');
const loginError = ref('');
const pairingError = ref('');
const passwordForgotForm = ref();
const { isPending, mutateAsync: login } = useLogin();
const { mutateAsync: sendPasswordReset, isPending: sendingPasswordReset } =
  useSendPasswordResetMail();
const { mutateAsync: exchangeDeviceCode, isPending: isDevicePairingPending } =
  useExchangeDeviceAuthCode();

const oauthCode = router.currentRoute.value.query?.code;
const isOauthCallback = typeof oauthCode === 'string';
const { mutateAsync: oauthCallback, isPending: isOAuthLoading } = useOAuthCallback();
if (isOauthCallback) {
  oauthCallback(window.location.search)
    .then(async () => await router.push({ name: 'home' }))
    .catch(() => {
      router.replace({ name: 'login' });
    });
}

watch(authPanel, () => {
  loginError.value = '';
  pairingError.value = '';
});

const onLogin = async (values: LoginDto) => {
  loginError.value = '';
  const isDemoUser = values.email.toLowerCase() === 'apple@carvia.de';
  try {
    if (isDemoUser && window.location.origin !== STAGING_APP_ORIGIN) {
      const url = new URL(`${STAGING_APP_ORIGIN}/login`);
      url.searchParams.set('email', values.email);
      url.searchParams.set('password', values.password);
      window.location.assign(url.toString());
      return;
    }
    await login({
      email: values.email,
      password: values.password,
    });
    await router.push({ name: 'home' });
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      loginError.value = t('wrongCredentials');
      return;
    }
    loginError.value = t('genericLoginError');
  }
};

onMounted(async () => {
  if (isOauthCallback) return;
  const creds = getCredentialsFromQuery(router.currentRoute.value.query);
  if (!creds) return;
  await router.replace({ name: 'login', query: {} });
  await onLogin(creds);
});

const onPasswordReset = async (values: SendPasswordResetMailDto) => {
  await sendPasswordReset(values);
  passwordForgotForm.value.resetPasswordResetForm();
};

const onDevicePairing = async ({ code }: { code: string }) => {
  pairingError.value = '';
  try {
    await exchangeDeviceCode(code);
    await router.push({ name: 'home' });
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      pairingError.value = t('invalidPairingCode');
      return;
    }
    pairingError.value = t('genericPairingError');
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "welcomeBack": "Welcome back",
    "pairDeviceLink": "Sign in with a device pairing code",
    "loggingIn": "Driver's license please...",
    "wrongCredentials": "Wrong username or password.",
    "genericLoginError": "Login failed. Please try again.",
    "invalidPairingCode": "Invalid or expired pairing code.",
    "genericPairingError": "Could not sign in with this code. Please try again."
  },
  "de": {
    "welcomeBack": "Willkommen zurück",
    "pairDeviceLink": "Mit Gerätekopplungscode anmelden",
    "loggingIn": "Einmal den Führerschein bitte...",
    "wrongCredentials": "Falscher Benutzername oder Passwort.",
    "genericLoginError": "Anmeldung fehlgeschlagen. Bitte versuche es erneut.",
    "invalidPairingCode": "Ungültiger oder abgelaufener Kopplungscode.",
    "genericPairingError": "Anmeldung mit diesem Code fehlgeschlagen. Bitte versuche es erneut."
  },
  "es": {
    "welcomeBack": "Bienvenido de nuevo",
    "pairDeviceLink": "Iniciar sesión con un código de emparejamiento",
    "loggingIn": "El permiso de conducir, por favor…",
    "wrongCredentials": "Credenciales incorrectas.",
    "genericLoginError": "Error de inicio de sesión. Por favor, inténtalo de nuevo.",
    "invalidPairingCode": "Código de emparejamiento no válido o caducado.",
    "genericPairingError": "No se pudo iniciar sesión con este código. Por favor, inténtalo de nuevo."
  }
}
</i18n>
